// @ts-check
import { createPortal } from 'react-dom';
import { useRegisterUnsavedChanges } from '../../context/UnsavedChangesContext';

/**
 * Shared modal shell for the whole admin app.
 *
 * Exists to fix, and keep fixed, two problems every hand-rolled modal in this
 * codebase used to have independently:
 *
 *  1. Stacking bugs — a modal rendered inline in the page DOM can end up behind
 *     any ancestor (or sibling, like the app sidebar) that has its own z-index
 *     quirk, because CSS stacking is relative to where the modal happens to
 *     live in the tree. Portaling straight to document.body sidesteps that
 *     category of bug entirely, permanently — no future z-index mistake
 *     anywhere else in the tree can trap a Modal again.
 *  2. Inconsistent mobile behavior — every modal used its own bespoke
 *     max-width/max-height/backdrop combination, several with no responsive
 *     handling at all, making the admin unusable on a phone. This defaults to
 *     a full-screen sheet below the `sm` breakpoint and a centered, max-height
 *     card above it.
 *
 * Callers own their own header/body/footer markup — this only owns the outer
 * shell (backdrop, centering, sizing, scroll). See ModalDrawer below for the
 * slide-in-from-the-side variant.
 */

const MAX_WIDTH = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
};

/**
 * @param {{
 *   onClose?: () => void,
 *   children: React.ReactNode,
 *   maxWidth?: keyof typeof MAX_WIDTH,
 *   zIndexClass?: string,
 *   closeOnBackdrop?: boolean,
 *   fullHeight?: boolean,
 *   className?: string,
 *   hasUnsavedChanges?: boolean,
 * }} props
 */
const Modal = ({
  onClose,
  children,
  maxWidth = '2xl',
  zIndexClass = 'z-50',
  closeOnBackdrop = true,
  fullHeight = true,
  className = '',
  hasUnsavedChanges,
}) => {
  // Opt-in: pass hasUnsavedChanges to protect this modal's content against a
  // sidebar click, back-button press, or refresh while it holds unsaved
  // input. Omitting the prop registers nothing — existing callers (delete
  // confirmations, read-only drawers) are unaffected.
  useRegisterUnsavedChanges(hasUnsavedChanges ?? false);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 bg-black/60 flex items-center justify-center overflow-y-auto sm:p-4 ${zIndexClass}`}
      onClick={
        closeOnBackdrop
          ? (e) => {
              if (e.target === e.currentTarget) onClose?.();
            }
          : undefined
      }
    >
      <div
        className={`bg-card text-card-foreground w-full ${fullHeight ? 'h-full sm:h-auto' : ''} sm:mx-4 sm:my-8 max-h-full sm:max-h-[85vh] overflow-y-auto sm:rounded-xl ${MAX_WIDTH[maxWidth] || MAX_WIDTH['2xl']} ${className}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

/**
 * Slide-in-from-the-side panel (e.g. a detail drawer) instead of a centered
 * card — same portal/z-index/backdrop guarantees as Modal, full-height and
 * full-width below `sm`, a fixed-width panel above it.
 * @param {{
 *   onClose?: () => void,
 *   children: React.ReactNode,
 *   maxWidth?: keyof typeof MAX_WIDTH,
 *   side?: 'left' | 'right',
 *   zIndexClass?: string,
 *   closeOnBackdrop?: boolean,
 *   className?: string,
 * }} props
 */
export const ModalDrawer = ({
  onClose,
  children,
  maxWidth = 'md',
  side = 'right',
  zIndexClass = 'z-50',
  closeOnBackdrop = true,
  className = '',
}) => {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 bg-black/60 flex ${side === 'right' ? 'justify-end' : 'justify-start'} ${zIndexClass}`}
      onClick={
        closeOnBackdrop
          ? (e) => {
              if (e.target === e.currentTarget) onClose?.();
            }
          : undefined
      }
    >
      <div
        className={`bg-card text-card-foreground w-full h-full ${MAX_WIDTH[maxWidth] || MAX_WIDTH['md']} overflow-y-auto ${className}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
